import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, LogOut, DollarSign, TrendingUp, Calendar, Target } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "../config";
import ModeloOnboardingWizard from "../components/ModeloOnboardingWizard";

const API = `${BACKEND_URL}/api`;

function ModeloDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [modelInfo, setModelInfo] = useState(null);
  const [historialReportes, setHistorialReportes] = useState([]);
  const [misMetas, setMisMetas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "modelo") {
      navigate("/");
      return;
    }
    fetchModelData();
  }, [user, navigate]);

  const fetchModelData = async () => {
    try {
      setLoading(true);
      
      // Obtener información de la modelo (self-service endpoint — incluye onboarding_completed)
      if (user.modelo_id) {
        try {
          const modelResponse = await axios.get(`${API}/models/me/profile`, {
            withCredentials: true
          });
          setModelInfo(modelResponse.data);
        } catch (err) {
          // Fallback al endpoint público (por si el modelo aún no tiene modelo_id sincronizado)
          const fb = await axios.get(`${API}/models/${user.modelo_id}`, {
            withCredentials: true
          });
          setModelInfo(fb.data);
        }
      }

      // Obtener mis metas (backend ya filtra por modelo_id automáticamente para rol=modelo)
      try {
        const metasRes = await axios.get(`${API}/model-goals`, { withCredentials: true });
        setMisMetas(Array.isArray(metasRes.data) ? metasRes.data : []);
      } catch (err) {
        console.error("Error metas:", err);
        setMisMetas([]);
      }

      // Obtener historial de reportes cerrados
      const historialResponse = await axios.get(`${API}/contabilidad/quincenal/historial`, {
        withCredentials: true
      });
      
      // Filtrar solo los reportes de esta modelo
      const misReportes = historialResponse.data
        .map(periodo => ({
          ...periodo,
          reportes: periodo.reportes.filter(r => r.modelo_id === user.modelo_id)
        }))
        .filter(periodo => periodo.reportes.length > 0);
      
      setHistorialReportes(misReportes);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar información");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    logout();
    navigate("/");
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-xl font-semibold text-white">Cargando...</div>
      </div>
    );
  }

  // Calcular totales
  const totalGenerado = historialReportes.reduce((sum, periodo) => {
    return sum + periodo.reportes.reduce((s, r) => s + (r.valor_neto || 0), 0);
  }, 0);

  const totalGanancias = historialReportes.reduce((sum, periodo) => {
    return sum + periodo.reportes.reduce((s, r) => s + (r.valor_neto_modelo || 0), 0);
  }, 0);

  const totalAgencia = historialReportes.reduce((sum, periodo) => {
    return sum + periodo.reportes.reduce((s, r) => s + (r.valor_neto_agencia || 0), 0);
  }, 0);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Wizard obligatorio de bienvenida — se muestra solo en el primer login */}
      {modelInfo && modelInfo.onboarding_completed === false && (
        <ModeloOnboardingWizard
          initialData={modelInfo}
          modelName={user.name}
          onComplete={() => {
            // Marcar localmente como completado + recargar perfil por si acaso
            setModelInfo({ ...modelInfo, onboarding_completed: true });
            fetchModelData();
          }}
        />
      )}

      {/* Header */}
      <div className="bg-black border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img 
                src="/logo.jpeg" 
                alt="JG Models Logo" 
                className="h-12 w-auto rounded-lg object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{fontFamily: 'Inter, sans-serif'}}>
                  {user.name}
                </h1>
                <p className="text-sm text-gray-400">Panel de Modelo</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800 flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bienvenida */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Bienvenida, {user.name}</h1>
          <p className="text-gray-400">Panel de información de ganancias</p>
        </div>

        {/* Cards de Resumen */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-2 border-green-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-300 text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Generado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                ${totalGenerado.toFixed(2)}
              </div>
              <p className="text-xs text-gray-400 mt-1">Histórico completo</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border-2 border-blue-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-300 text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Tus Ganancias
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">
                ${totalGanancias.toFixed(2)}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {modelInfo?.porcentaje_agencia ? `${100 - modelInfo.porcentaje_agencia}%` : ''} de las ventas
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-2 border-purple-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-300 text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Periodos Cerrados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-400">
                {historialReportes.length}
              </div>
              <p className="text-xs text-gray-400 mt-1">Quincenas registradas</p>
            </CardContent>
          </Card>
        </div>

        {/* Mis Metas */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8" data-testid="mis-metas-section">
          <CardHeader>
            <CardTitle className="text-white text-2xl flex items-center gap-2">
              <Target className="h-6 w-6 text-pink-400" />
              Mis Metas
            </CardTitle>
            <p className="text-sm text-gray-400 mt-2">
              Metas asignadas por la agencia y tu progreso en tiempo real
            </p>
          </CardHeader>
          <CardContent>
            {misMetas.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-3">🎯</div>
                <p className="text-gray-400">Aún no tienes metas asignadas</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {misMetas.map((meta) => {
                  const goalAmt = Number(meta.goal_amount || 0);
                  const curr = Number(meta.current_amount || 0);
                  const pct = goalAmt > 0 ? Math.min((curr / goalAmt) * 100, 100) : 0;
                  const isCompleted = pct >= 100;
                  const barColor = isCompleted
                    ? 'bg-green-500'
                    : pct >= 75
                    ? 'bg-blue-500'
                    : pct >= 50
                    ? 'bg-purple-500'
                    : 'bg-orange-500';
                  return (
                    <Card key={meta.id} className="bg-zinc-800 border-zinc-700" data-testid={`mi-meta-${meta.id}`}>
                      <CardContent className="pt-5 space-y-3">
                        <div>
                          <div className="text-white font-bold text-base">
                            {meta.period_name || `${meta.fecha_inicio} → ${meta.fecha_fin}`}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {meta.fecha_inicio} - {meta.fecha_fin}
                          </div>
                        </div>
                        <div>
                          <div className="flex items-baseline justify-between mb-1.5">
                            <span className="text-xs text-gray-400 uppercase">Progreso</span>
                            <span className="text-lg font-bold text-white">{Math.round(pct)}%</span>
                          </div>
                          <div className="h-2.5 bg-zinc-900 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${barColor} transition-all duration-500`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-700">
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase">Meta</div>
                            <div className="text-sm font-semibold text-white">
                              ${goalAmt.toLocaleString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] text-gray-500 uppercase">Logrado</div>
                            <div className="text-sm font-bold text-emerald-400">
                              ${curr.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        {meta.notas && (
                          <div className="text-xs text-gray-400 italic border-t border-zinc-700 pt-2">
                            {meta.notas}
                          </div>
                        )}
                        {isCompleted && (
                          <div className="py-1.5 bg-green-500/15 rounded text-center">
                            <span className="text-green-400 text-xs font-semibold">
                              🏆 ¡Meta Cumplida!
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Historial de Reportes */}
        <Card className="bg-zinc-900 border-zinc-800">          <CardHeader>
            <CardTitle className="text-white text-2xl">📊 Historial de Facturación por Periodos</CardTitle>
            <p className="text-sm text-gray-400 mt-2">
              Consulta tus ganancias en cada periodo quincenal cerrado
            </p>
          </CardHeader>
          <CardContent>
            {historialReportes.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-gray-400 text-lg">
                  Aún no hay periodos cerrados
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Los reportes aparecerán aquí cuando tu administrador cierre un periodo
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {historialReportes.map((periodo, index) => {
                  const reporte = periodo.reportes[0]; // Solo hay un reporte por modelo
                  return (
                    <Card key={index} className="bg-zinc-800 border-zinc-700">
                      <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-5 w-5 text-purple-400" />
                              <h3 className="text-white font-bold text-lg">
                                {new Date(periodo.quincena_inicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - {new Date(periodo.quincena_fin).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </h3>
                            </div>
                            <p className="text-gray-400 text-sm">
                              Cerrado el {new Date(periodo.fecha_cierre).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                              <div className="text-gray-400 text-xs mb-1">Total Generado</div>
                              <div className="text-white font-bold text-lg">
                                ${(reporte.valor_neto || 0).toFixed(2)}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-400 text-xs mb-1">Tus Ganancias</div>
                              <div className="text-blue-400 font-bold text-lg">
                                ${(reporte.valor_neto_modelo || 0).toFixed(2)}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-400 text-xs mb-1">Agencia</div>
                              <div className="text-green-400 font-bold text-lg">
                                ${(reporte.valor_neto_agencia || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ModeloDashboard;
